import React, { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from "react";
import axios from "axios";
import html2pdf from "html2pdf.js";

// Components (Non-lazy)
import ChangelogModal from './ChangelogModal';
import SuggestionModal from './SuggestionModal';
import PushNotificationPrompt from './PushNotificationPrompt';
import ForceReacceptanceModal from './legal/ForceReacceptanceModal';
import Navbar from './layout/Navbar';
import LazyTab from './layout/LazyTab';
import Header from './layout/Header';
import CommunicatorWidget from './CommunicatorWidget';
import MattersFab from './MattersFab';
import SalesRankingPopup from './sales/SalesRankingPopup';
import { FadeIn, LoadingSpinner } from './ui/LoadingComponents';
import BackToPreviousTab from './ui/BackToPreviousTab';
import { TAB_LABELS } from '../constants/tabLabels';
import ModulePinGate from './ModulePinGate';
import { useUnreadModules } from '../hooks/useUnreadModules';

// Mobile Components
import BottomNav from './mobile/BottomNav';
import HamburgerMenu from './mobile/HamburgerMenu';
import { useIsMobile } from '../hooks/useMediaQuery';

// Lazy-loaded Tab Components
// === BUNDLE SPLITTING — łączę 42 chunków w 5 logicznych grup ===
// Każdy `webpackChunkName` daje jeden plik JS. Klik w pierwszą zakładkę z grupy → cała grupa już w cache.

// Grupa 1: CORE (najczęściej używane — codzienne moduły)
const SalesTab = lazy(() => import(/* webpackChunkName: "core" */ './tabs/SalesTab'));
const RestaurantMattersTab = lazy(() => import(/* webpackChunkName: "core" */ './tabs/RestaurantMattersTab'));
const InvoicesTab = lazy(() => import(/* webpackChunkName: "core" */ './tabs/InvoicesTab'));
const EventsCateringTab = lazy(() => import(/* webpackChunkName: "core" */ './tabs/EventsCateringTab'));
const DeliverySurveyTab = lazy(() => import(/* webpackChunkName: "core" */ './tabs/SurveysTab'));
const ClubAdminTab = lazy(() => import(/* webpackChunkName: "core" */ './tabs/ClubAdminTab'));
const RemindersTab = lazy(() => import(/* webpackChunkName: "core" */ './tabs/RemindersTab'));
const TipsTab = lazy(() => import(/* webpackChunkName: "core" */ './tabs/TipsTab'));
const SummaryTab = lazy(() => import(/* webpackChunkName: "core" */ './tabs/SummaryTab'));
const DashboardTab = lazy(() => import(/* webpackChunkName: "core" */ './tabs/DashboardTab'));
const HomeTab = lazy(() => import(/* webpackChunkName: "core" */ './tabs/HomeTab'));
const DailyBriefingTab = lazy(() => import(/* webpackChunkName: "core" */ './tabs/DailyBriefingTab'));

// Grupa 2: MANAGER (panel zarządzania zespołem)
const ManagerTab = lazy(() => import(/* webpackChunkName: "manager" */ './manager/ManagerTab'));
const ManagerAnalyticsTab = lazy(() => import(/* webpackChunkName: "manager" */ './tabs/ManagerAnalyticsTab'));
const ManagerPerformanceTab = lazy(() => import(/* webpackChunkName: "manager" */ './tabs/ManagerPerformanceTab'));
const EmployeesTab = lazy(() => import(/* webpackChunkName: "manager" */ './tabs/EmployeesTab'));
const RecruitmentTab = lazy(() => import(/* webpackChunkName: "manager" */ './tabs/RecruitmentTab'));
const RecruitmentApplicationsTab = lazy(() => import(/* webpackChunkName: "manager" */ './tabs/RecruitmentApplicationsTab'));
const ReviewsTab = lazy(() => import(/* webpackChunkName: "manager" */ './tabs/ReviewsTab'));
const ReportsTab = lazy(() => import(/* webpackChunkName: "manager" */ './tabs/ReportsTab'));
const TeamEngagementTab = lazy(() => import(/* webpackChunkName: "manager" */ './tabs/TeamEngagementTab'));

// Grupa 3: OPERATIONS (codzienne operacje restauracji)
const BillardTab = lazy(() => import(/* webpackChunkName: "operations" */ './tabs/BillardTab'));
const JerryDriveTab = lazy(() => import(/* webpackChunkName: "operations" */ './tabs/JerryDriveTab'));
const WarehouseTab = lazy(() => import(/* webpackChunkName: "operations" */ './tabs/WarehouseTab'));
const WarehouseProtocolsTab = lazy(() => import(/* webpackChunkName: "operations" */ './tabs/WarehouseProtocolsTab'));
const KitchenLossesTab = lazy(() => import(/* webpackChunkName: "operations" */ './tabs/KitchenLossesTab'));
const KitchenLossesReportsTab = lazy(() => import(/* webpackChunkName: "operations" */ './tabs/KitchenLossesReportsTab'));
const BeerCleaningTab = lazy(() => import(/* webpackChunkName: "operations" */ './tabs/BeerCleaningTab'));
const DeliveriesTab = lazy(() => import(/* webpackChunkName: "operations" */ './tabs/DeliveriesTab'));
const GlassTab = lazy(() => import(/* webpackChunkName: "operations" */ './tabs/GlassTab'));
const ClothingTab = lazy(() => import(/* webpackChunkName: "operations" */ './tabs/ClothingTab'));
const InventoryTab = lazy(() => import(/* webpackChunkName: "operations" */ './tabs/InventoryTab'));
const MonthlyClosingTab = lazy(() => import(/* webpackChunkName: "operations" */ './tabs/MonthlyClosingTab'));
const CashTab = lazy(() => import(/* webpackChunkName: "operations" */ './tabs/CashTab'));
const FilesTab = lazy(() => import(/* webpackChunkName: "operations" */ './tabs/FilesTab'));

// Grupa 4: CONTENT (treści, materiały, komunikacja)
const TrainingTab = lazy(() => import(/* webpackChunkName: "content" */ './tabs/TrainingTab'));
const MaterialsTab = lazy(() => import(/* webpackChunkName: "content" */ './tabs/MaterialsTab'));
const ChangelogTab = lazy(() => import(/* webpackChunkName: "content" */ './tabs/ChangelogTab'));
const GraphicProjectsTab = lazy(() => import(/* webpackChunkName: "content" */ './tabs/GraphicProjectsTab'));
const PromotionsTab = lazy(() => import(/* webpackChunkName: "content" */ './tabs/PromotionsTab'));
const DelikatesyTab = lazy(() => import(/* webpackChunkName: "content" */ './tabs/DelikatesyTab'));
const CommunicatorTab = lazy(() => import(/* webpackChunkName: "content" */ './tabs/CommunicatorTab'));
const MarketingTab = lazy(() => import(/* webpackChunkName: "content" */ './tabs/MarketingTab'));
const DailySettlementTab = lazy(() => import(/* webpackChunkName: "content" */ './tabs/DailySettlementTab'));

// Grupa 5: ADMIN (ustawienia, eksport, narzędzia)
const SettingsTab = lazy(() => import(/* webpackChunkName: "admin" */ './tabs/SettingsTab'));
const ExportTab = lazy(() => import(/* webpackChunkName: "admin" */ './tabs/ExportTab'));
const DevelopmentTab = lazy(() => import(/* webpackChunkName: "admin" */ './tabs/DevelopmentTab'));
const TutorialTab = lazy(() => import(/* webpackChunkName: "admin" */ './tabs/TutorialTab'));
const InventoryConversionModal = lazy(() => import(/* webpackChunkName: "admin" */ './InventoryConversionModal'));

// Utils
import { pad, todayISO, timeHHMMSS, fmtPLN, round2, waiterFullName, splitAmount } from '../utils/helpers';
import { isSessionValid } from '../utils/session';
import { API, LOGO_URL, WAITER_SHARE, KITCHEN_SHARE } from '../utils/constants';
import { APP_VERSION } from '../version';
import { generateTipsPDF, generateBillardPDF as generateBillardPDFUtil } from '../utils/pdfGenerator';
import { generateDayPDF as generateDayPDFCustom, generateBillardPDF as generateBillardPDFCustom } from '../utils/customPdfGenerators';

// Context
import { useApp } from '../context/AppStateContext';

// Custom hooks (refactored handlers)
import { useUserActions } from '../hooks/useUserActions';
import { useSettingsActions } from '../hooks/useSettingsActions';
import { useEntriesActions } from '../hooks/useEntriesActions';
import { useTabRouting } from '../hooks/useTabRouting';
import { useAppEffects } from '../hooks/useAppEffects';
import { useDerivedData } from '../hooks/useDerivedData';
import { useReportsUrlSync } from '../hooks/useReportsUrlSync';

// PDF generators
import { generateThermalPDF as generateThermalPDFUtil } from '../utils/thermalPDF';
import { generateReportPDF } from '../utils/exportReportPDF';




// ============ MAIN APP ============
function MainApp({ onLogout }) {
  // Mobile detection
  const isMobile = useIsMobile();
  
  // Changelog modal
  const [isChangelogOpen, setIsChangelogOpen] = useState(false);
  const [showConversionModal, setShowConversionModal] = useState(false);
  
  // Suggestion modal
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  
  // Sidebar state for mobile
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Sidebar collapse state for desktop (persist across reloads)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('mrjerry_sidebar_collapsed') === '1'; }
    catch { return false; }
  });
  const toggleSidebar = useCallback(() => {
    const isMobileNow = typeof window !== 'undefined' && window.innerWidth <= 768;
    if (isMobileNow) {
      setIsSidebarOpen((v) => !v);
    } else {
      setIsSidebarCollapsed((v) => {
        const next = !v;
        try { localStorage.setItem('mrjerry_sidebar_collapsed', next ? '1' : '0'); } catch { /* ignore */ }
        // Toggle body class so main-content shifts back to full width
        if (typeof document !== 'undefined') {
          document.body.classList.toggle('sidebar-collapsed-body', next);
        }
        return next;
      });
    }
  }, []);
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.classList.toggle('sidebar-collapsed-body', isSidebarCollapsed);
    }
  }, [isSidebarCollapsed]);
  
  // Hamburger menu state
  const [isHamburgerOpen, setIsHamburgerOpen] = useState(false);
  
  // Get all state and helpers from Context
  const {
    // Core data
    waiters, setWaiters,
    entries, setEntries,
    closedDays, setClosedDays,
    users, setUsers,
    billardEntries, setBillardEntries,
    clothingItems, setClothingItems,
    jobPositions, setJobPositions,
    clothingTypes, setClothingTypes,
    glassTypes, setGlassTypes,
    archivedBackups, setArchivedBackups,
    auditLogs, setAuditLogs,
    
    // UI State
    activeTab, setActiveTab: setActiveTabInternal,
    currentDateTime, setCurrentDateTime,
    showInactive, setShowInactive,
    expandedCategories, setExpandedCategories,
    showDeletedWaiters, setShowDeletedWaiters,
    showDeletedEntries, setShowDeletedEntries,
    showDeletedBillard, setShowDeletedBillard,
    showDeletedClothing, setShowDeletedClothing,
    expandUsers, setExpandUsers,
    expandSystemLogs, setExpandSystemLogs,
    expandJobPositions, setExpandJobPositions,
    expandClothingTypes, setExpandClothingTypes,
    expandGlassTypes, setExpandGlassTypes,
    
    // Form States - Tips
    tipDate, setTipDate,
    selectedWaiter, setSelectedWaiter,
    tipsAmount, setTipsAmount,
    notes, setNotes,
    
    // Form States - Waiter
    firstName, setFirstName,
    lastName, setLastName,
    branch, setBranch,
    birthDate, setBirthDate,
    waiterStatus, setWaiterStatus,
    workerCategory, setWorkerCategory,
    
    // Form States - Reports
    fromDate, setFromDate,
    toDate, setToDate,
    reportType, setReportType,
    
    // Form States - Export
    exportFilter, setExportFilter,
    
    // Form States - Settings (Users)
    newUsername, setNewUsername,
    newPassword, setNewPassword,
    newRole, setNewRole,
    editingUserId, setEditingUserId,
    editPassword, setEditPassword,
    editingPermissionsUserId, setEditingPermissionsUserId,
    editingPermissions, setEditingPermissions,
    
    // Form States - Settings (Job Positions)
    newJobPosition, setNewJobPosition,
    editingJobPositionId, setEditingJobPositionId,
    editJobPositionName, setEditJobPositionName,
    
    // Form States - Settings (Clothing Types)
    newClothingType, setNewClothingType,
    editingClothingTypeId, setEditingClothingTypeId,
    editClothingTypeName, setEditClothingTypeName,
    
    // Form States - Settings (Glass Types)
    newGlassType, setNewGlassType,
    editingGlassTypeId, setEditingGlassTypeId,
    editGlassTypeName, setEditGlassTypeName,
    
    // Form States - Settings (Cleanup)
    daysToKeep, setDaysToKeep,
    
    // Backup
    showBackupReminder, setShowBackupReminder,
    lastBackupDate, setLastBackupDate,
    
    // Refs
    restoreFileRef,
    
    // Helpers
    user,
    location,
    locationName,
    locationIcon,
    isManager,
    hasPermission,
    getCategoryDisplayName,
    categoryDisplayNames,
    
    // Actions
    fetchGlassTypes,
    fetchBackupData,
    fetchAuditLog,
    triggerGlobalRefresh,
    refreshKey,
  } = useApp();

  // URL <-> activeTab sync (deep-links, back/forward button)
  const setActiveTab = useTabRouting(activeTab, setActiveTabInternal);

  // ==================== Unread indicators (zielona kropka w sidebarze) ====================
  const { flags: unreadFlags, markSeen: markModuleSeen } = useUnreadModules({
    userId: user?.id || user?.username,
    location,
  });

  // Po wejściu w moduł — zaznacz jako przeczytane (zniknie kropka)
  useEffect(() => {
    if (activeTab === 'restaurant_matters') markModuleSeen('restaurant_matters');
    if (activeTab === 'development' || activeTab === 'rozwoj_zespolu') markModuleSeen('development');
  }, [activeTab, markModuleSeen]);

  // ==================== Prefetch chunków po zalogowaniu (idle callback) ====================
  // Po zalogowaniu, gdy CPU jest bezczynne, wstępnie ładuje najczęściej używane chunki
  // żeby przy pierwszym kliknięciu zakładki ekran wyświetlał się natychmiast.
  useEffect(() => {
    if (!user || !location) return undefined;
    const schedule = (cb) => {
      if (typeof window.requestIdleCallback === 'function') {
        return window.requestIdleCallback(cb, { timeout: 4000 });
      }
      return window.setTimeout(cb, 1500);
    };
    const cancel = (id) => {
      if (typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(id);
      } else {
        window.clearTimeout(id);
      }
    };
    const handles = [];
    // Najpierw CORE (najczęściej używane — Sprzedaż, Sprawy, Faktury, Przypomnienia, Briefing)
    handles.push(schedule(() => { import(/* webpackChunkName: "core" */ './tabs/SalesTab'); }));
    // Potem OPERATIONS i MANAGER
    handles.push(schedule(() => { import(/* webpackChunkName: "operations" */ './tabs/JerryDriveTab'); }));
    handles.push(schedule(() => { import(/* webpackChunkName: "manager" */ './manager/ManagerTab'); }));
    // Na końcu CONTENT i ADMIN
    handles.push(schedule(() => { import(/* webpackChunkName: "content" */ './tabs/MaterialsTab'); }));
    handles.push(schedule(() => { import(/* webpackChunkName: "admin" */ './tabs/SettingsTab'); }));
    return () => { handles.forEach(cancel); };
  }, [user, location]);


  // ==================== Communicator: unread badge polling (co 10s) ====================
  const [communicatorUnread, setCommunicatorUnread] = useState(0);
  useEffect(() => {
    if (!user?.username || !location) return;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${API}/communicator/unread-count`, {
          params: { location, username: user.username },
        });
        if (!cancelled) setCommunicatorUnread(res.data?.unreadCount || 0);
      } catch { /* silent */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 10_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user?.username, location, activeTab]);

  // ==================== UX polish: scroll-to-top on tab change ====================
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Smooth scroll do góry — tylko gdy użytkownik jest poza viewport-em, by nie skakać niepotrzebnie
    if (window.scrollY > 80) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeTab]);

  // ==================== Reminders: count polling (co 60s) ====================
  const [remindersCount, setRemindersCount] = useState(0);
  useEffect(() => {
    if (!location) return;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${API}/reminders/count`, { params: { location } });
        if (!cancelled) setRemindersCount(res.data?.count || 0);
      } catch { /* silent */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [location, activeTab]);

  // ==================== Resolutions feed (Sprawy → zrealizowane) ====================
  // Co 60s pobieramy ostatnie zrealizowane sprawy w lokalu. Pracownicy widzą
  // krótkie komunikaty "{tytuł} — zrealizował {manager}" w dzwoneczku 🔔
  const [resolutions, setResolutions] = useState([]);
  // ==================== Inquiries (answered) feed ====================
  const [inquiries, setInquiries] = useState([]);
  const lastSeenKey = location ? `mrj_resolutions_seen_${location}` : null;
  const lastSeenInquiriesKey = location ? `mrj_inquiries_seen_${location}` : null;
  useEffect(() => {
    if (!location) return;
    let cancelled = false;
    const fetchAll = async () => {
      try {
        const [resR, inqR] = await Promise.all([
          axios.get(`${API}/restaurant-matters/resolutions`, { params: { location, limit: 15 } }),
          axios.get(`${API}/restaurant-inquiries/answered-feed`, { params: { location, limit: 15 } }),
        ]);
        if (!cancelled) {
          setResolutions(resR.data?.items || []);
          setInquiries(inqR.data?.items || []);
        }
      } catch { /* silent */ }
    };
    fetchAll();
    const interval = setInterval(fetchAll, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [location]);

  const markResolutionsSeen = useCallback(() => {
    const now = new Date().toISOString();
    if (lastSeenKey) {
      try { localStorage.setItem(lastSeenKey, now); } catch { /* */ }
    }
    if (lastSeenInquiriesKey) {
      try { localStorage.setItem(lastSeenInquiriesKey, now); } catch { /* */ }
    }
  }, [lastSeenKey, lastSeenInquiriesKey]);

  // Sync /raporty ?from= ?to= with fromDate/toDate (shareable links)
  useReportsUrlSync(fromDate, setFromDate, toDate, setToDate);

  // ==================== Side effects (fetch, timers, session) ====================
  const { fetchData: fetchDataLegacy } = useAppEffects({
    user, location, isManager, activeTab,
    tipDate, setTipDate,
    setWaiters, setEntries, setClosedDays, setUsers,
    setBillardEntries, setClothingItems,
    setJobPositions, setClothingTypes,
    setCurrentDateTime, setShowBackupReminder,
    showDeletedWaiters, showDeletedEntries, showDeletedBillard, showDeletedClothing,
    fetchBackupData, fetchAuditLog, fetchGlassTypes,
    onLogout,
  });

  // ==================== Set data-location on root for per-location theming ====================
  useEffect(() => {
    if (location) {
      document.documentElement.setAttribute('data-location', location);
    }
    return () => {
      document.documentElement.removeAttribute('data-location');
    };
  }, [location]);

  // ==================== Derived data (memoized) ====================
  const splitPreview = splitAmount(Number(tipsAmount || 0), location);
  const {
    isDayClosed,
    getClosedMeta,
    waiterById,
    displayWaiters,
    tipsWaiters,
    dayEntries,
    dayTotals,
    filteredEntries,
    overallTotals,
    dailyTotals,
    waiterTotals,
  } = useDerivedData({
    waiters, entries, closedDays,
    tipDate, fromDate, toDate, showInactive,
  });

  // ========== Billard Functions ==========
  
  // Thermal PDF wrapper (defined early so useEntriesActions can use it)
  const generateThermalPDF = async (targetDate) => {
    return generateThermalPDFUtil({
      targetDate,
      entries,
      locationName,
      username: user.username,
    });
  };

  // Extracted entry / waiter handlers
  const {
    handleSaveEntry,
    handleCloseDay,
    handleOpenDay,
    handleDeleteEntry,
    handleRestoreEntry,
    handleRestoreWaiter,
    handleRestoreBillard,
    handleRestoreClothing,
    handleAddWaiter,
    handleSetWaiterStatus,
  } = useEntriesActions({
    user, location,
    tipDate,
    selectedWaiter, setSelectedWaiter,
    tipsAmount, setTipsAmount,
    notes, setNotes,
    firstName, setFirstName,
    lastName, setLastName,
    branch, setBranch,
    birthDate, setBirthDate,
    waiterStatus,
    workerCategory, setWorkerCategory,
    isDayClosed,
    dayEntries,
    dayTotals,
    generateThermalPDF,
    refresh: () => fetchDataLegacy(),
  });

  // Extracted user-management handlers
  const {
    handleAddUser,
    handleDeleteUser,
    handleChangeUserPassword,
    handleUpdateUserPermissions,
    togglePermission,
  } = useUserActions({
    location,
    newUsername, setNewUsername,
    newPassword, setNewPassword,
    newRole,
    editPassword, setEditPassword,
    setEditingUserId,
    editingPermissions, setEditingPermissions,
    setEditingPermissionsUserId,
    refresh: () => fetchDataLegacy(),
  });

  // Extracted settings-page handlers (dictionaries, backup, cleanup, reset)
  const {
    handleAddJobPosition, handleDeleteJobPosition, handleUpdateJobPosition,
    handleAddClothingType, handleDeleteClothingType, handleUpdateClothingType,
    handleAddGlassType, handleUpdateGlassType, handleDeleteGlassType,
    handleConfirmBackup, handleBackupDownload,
    handleRestoreFromArchive, handleDeleteArchivedBackup,
    handleRestoreFile, handleResetAll,
    handleCleanupData, handleClearOldExports,
  } = useSettingsActions({
    user, location, locationName,
    newJobPosition, setNewJobPosition,
    editJobPositionName, setEditJobPositionName, setEditingJobPositionId,
    newClothingType, setNewClothingType,
    editClothingTypeName, setEditClothingTypeName, setEditingClothingTypeId,
    newGlassType, setNewGlassType,
    glassTypes, setGlassTypes,
    editGlassTypeName, setEditGlassTypeName, setEditingGlassTypeId,
    daysToKeep,
    closedDays,
    setShowBackupReminder,
    setLastBackupDate,
    refresh: () => fetchDataLegacy(),
    fetchBackupData,
  });

  const handleExportCSV = () => {
    const rows = [["Data", "Kelner", "Napiwki", "Kelner 60%", "Kuchnia 40%", "Kto notował", "Uwagi"]];
    for (const e of filteredEntries.sort((a, b) => a.date.localeCompare(b.date))) {
      const w = waiterById.get(e.waiterId);
      rows.push([e.date, w ? waiterFullName(w) : "?", fmtPLN(e.amount), fmtPLN(e.waiter), fmtPLN(e.kitchen), e.manager, e.notes || ""]);
    }
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `mrjerry-raport-${todayISO()}.csv`;
    a.click();
  };

  const generateDayPDF = async (targetDate) => {
    return generateDayPDFCustom(targetDate, entries, waiters, locationName, LOGO_URL);
  };

  const generateBillardPDF = async (startDate, endDate) => {
    return generateBillardPDFCustom(startDate, endDate, billardEntries, locationName, LOGO_URL);
  };


  const handleExportPDF = async () => {
    return generateReportPDF({
      filteredEntries,
      waiterById,
      overallTotals,
      waiterTotals,
      dailyTotals,
      fromDate,
      toDate,
      logoUrl: LOGO_URL,
    });
  };

  const dayMeta = getClosedMeta(tipDate);
  const rangeLabel = (fromDate || toDate) ? `${fromDate || "..."} → ${toDate || "..."}` : "Wszystkie dane";
  const rangeTotals = overallTotals(filteredEntries);

  // Handle data refresh
  const [isRefreshing, setIsRefreshing] = useState(false);
  const managerRefreshRef = useRef(null); // Ref to trigger Manager Tab refresh
  
  const handleRefreshData = async () => {
    if (isRefreshing) return; // Prevent multiple clicks
    
    setIsRefreshing(true);
    try {
      console.log('🔄 Manual refresh triggered');
      
      // Refresh main data (legacy core: tipy/pracownicy/billard/odzież itd.)
      await fetchDataLegacy();
      
      // Trigger global refresh for ALL tabs subscribing via refreshKey
      triggerGlobalRefresh();
      
      // If on Manager tab, also refresh Manager-specific data
      if (activeTab === 'manager' && managerRefreshRef.current) {
        console.log('🔄 Refreshing Manager Tab data...');
        await managerRefreshRef.current();
      }
      
      // Show success toast
      const toastDiv = document.createElement('div');
      toastDiv.className = 'refresh-toast';
      toastDiv.innerHTML = `
        <div style="
          position: fixed;
          top: 80px;
          right: 20px;
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.95) 0%, rgba(22, 163, 74, 0.95) 100%);
          color: white;
          padding: 12px 20px;
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(34, 197, 94, 0.4);
          z-index: 10000;
          font-size: 14px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 10px;
          animation: slideIn 0.3s ease-out;
        ">
          ✅ Dane odświeżone pomyślnie
        </div>
      `;
      document.body.appendChild(toastDiv);
      
      setTimeout(() => {
        toastDiv.style.animation = 'slideOut 0.3s ease-in';
        setTimeout(() => document.body.removeChild(toastDiv), 300);
      }, 2000);
      
    } catch (error) {
      console.error('❌ Error refreshing data:', error);
      
      // Show error toast
      const toastDiv = document.createElement('div');
      toastDiv.innerHTML = `
        <div style="
          position: fixed;
          top: 80px;
          right: 20px;
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(220, 38, 38, 0.95) 100%);
          color: white;
          padding: 12px 20px;
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(239, 68, 68, 0.4);
          z-index: 10000;
          font-size: 14px;
          font-weight: 600;
        ">
          ❌ Błąd podczas odświeżania
        </div>
      `;
      document.body.appendChild(toastDiv);
      
      setTimeout(() => {
        document.body.removeChild(toastDiv);
      }, 2000);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="app" data-testid="app-container">
      {/* Header Component */}
      <Header 
        user={user}
        location={location}
        locationName={locationName}
        locationIcon={locationIcon}
        currentDateTime={currentDateTime}
        appVersion={APP_VERSION}
        logoUrl={LOGO_URL}
        onLogout={onLogout}
        onChangelogClick={() => setIsChangelogOpen(true)}
        onSuggestionClick={() => setShowSuggestionModal(true)}
        onSidebarToggle={toggleSidebar}
        isSidebarOpen={isMobile ? isSidebarOpen : !isSidebarCollapsed}
        isMobile={isMobile}
        onRefreshData={handleRefreshData}
        isRefreshing={isRefreshing}
        communicatorUnread={communicatorUnread}
        remindersCount={remindersCount}
        resolutions={resolutions}
        inquiries={inquiries}
        lastSeenResolutionsKey={lastSeenKey}
        lastSeenInquiriesKey={lastSeenInquiriesKey}
        onResolutionsSeen={markResolutionsSeen}
        setActiveTab={setActiveTab}
        waiters={waiters}
        onMobileMenuToggle={setIsHamburgerOpen}
        isMobileMenuOpen={isHamburgerOpen}
      />

      {/* Navigation Component - Hide on mobile */}
      {!isMobile && (
        <Navbar 
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          hasPermission={hasPermission}
          location={location}
          user={user}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          isSidebarCollapsed={isSidebarCollapsed}
          communicatorUnread={communicatorUnread}
          remindersCount={remindersCount}
          unreadFlags={unreadFlags}
        />
      )}

      <main className="main-content">
        <BackToPreviousTab
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabLabels={TAB_LABELS}
          defaultTab="home"
        />
        <div key={`${activeTab}::${refreshKey}`} className="tab-transition-wrap">
        {/* HOME — strona powitalna (default) */}
        {activeTab === "home" && (
          <LazyTab fallbackMessage="Wystąpił błąd na stronie głównej">
            <HomeTab
              user={user}
              location={location}
              setActiveTab={setActiveTab}
              hasPermission={hasPermission}
            />
          </LazyTab>
        )}

        {/* DASHBOARD — tymczasowo wyłączone w 2.2.8 (fallback do Komunikatora) */}
        {false && activeTab === "dashboard" && (
          <LazyTab fallbackMessage="Wystąpił błąd w Dashboardzie">
              <DashboardTab
                user={user}
                location={location}
                setActiveTab={setActiveTab}
              />
            </LazyTab>
        )}

        {/* COMMUNICATOR */}
        {activeTab === "communicator" && (hasPermission('communicator') || hasPermission('manager') || hasPermission('settings') || isManager) && (
          <LazyTab fallbackMessage="Wystąpił błąd w Komunikatorze">
              <CommunicatorTab
                user={user}
                location={location}
                canManage={isManager || hasPermission('manager') || hasPermission('settings')}
              />
            </LazyTab>
        )}

        {/* REMINDERS */}
        {activeTab === "reminders" && (
          <LazyTab fallbackMessage="Wystąpił błąd w Przypomnieniach">
              <RemindersTab
                location={location}
                canEdit={isManager || hasPermission('reminders_edit') || hasPermission('settings')}
              />
            </LazyTab>
        )}

        {/* MANAGER ANALYTICS */}
        {activeTab === "manager_analytics" && (hasPermission('manager_analytics') || hasPermission('manager_analytics_view') || hasPermission('manager_analytics_edit') || hasPermission('manager') || hasPermission('settings') || isManager) && (
          <LazyTab fallbackMessage="Wystąpił błąd w Analizie managerskiej">
              <ModulePinGate
                moduleId="manager_analytics"
                location={location}
                title="📊 Analiza managerska — dostęp chroniony"
                onCancel={() => setActiveTab('manager')}
              >
                <ManagerAnalyticsTab
                  location={location}
                  currentUser={user}
                  canEdit={isManager || hasPermission('manager_analytics') || hasPermission('manager_analytics_edit') || hasPermission('manager') || hasPermission('settings')}
                />
              </ModulePinGate>
            </LazyTab>
        )}

        {/* MANAGER PERFORMANCE — codzienne wpisy kierownika + AI raport */}
        {activeTab === "manager_performance" && (hasPermission('manager_performance_view') || hasPermission('manager_performance_edit') || hasPermission('manager_performance_owner') || hasPermission('manager') || hasPermission('settings') || isManager) && (
          <LazyTab fallbackMessage="Wystąpił błąd w module Manager Performance">
              <ManagerPerformanceTab
                location={location}
                currentUser={user}
                canEdit={isManager || hasPermission('manager_performance_edit') || hasPermission('manager') || hasPermission('settings')}
                canViewOwner={isManager || hasPermission('manager_performance_owner') || hasPermission('manager') || hasPermission('settings')}
                canFillChecklist={isManager || hasPermission('manager_checklist_fill') || hasPermission('manager_checklist_manage') || hasPermission('manager') || hasPermission('settings')}
                canManageChecklist={isManager || hasPermission('manager_checklist_manage') || hasPermission('manager') || hasPermission('settings')}
                canViewResponsibilities={isManager || hasPermission('manager_responsibilities_view') || hasPermission('manager_responsibilities_manage') || hasPermission('manager') || hasPermission('settings')}
                canManageResponsibilities={isManager || hasPermission('manager_responsibilities_manage') || hasPermission('manager') || hasPermission('settings')}
              />
            </LazyTab>
        )}

        {/* DELIKATESY — TYLKO NOWY SĄCZ */}
        {activeTab === "delikatesy" && location === "nowysacz" && (hasPermission('delikatesy') || hasPermission('delikatesy_view') || hasPermission('delikatesy_edit') || hasPermission('manager') || hasPermission('settings') || isManager) && (
          <LazyTab fallbackMessage="Wystąpił błąd w module Delikatesy">
              <DelikatesyTab
                location={location}
                canEdit={isManager || hasPermission('delikatesy') || hasPermission('delikatesy_edit') || hasPermission('manager') || hasPermission('settings')}
              />
            </LazyTab>
        )}

        {/* TEAM ENGAGEMENT — Zaangażowanie zespołu */}
        {activeTab === "team_engagement" && (hasPermission('team_engagement') || hasPermission('team_engagement_manage') || hasPermission('manager') || hasPermission('settings') || isManager) && (
          <LazyTab fallbackMessage="Wystąpił błąd w module Zaangażowanie zespołu">
              <TeamEngagementTab
                location={location}
                canManage={isManager || hasPermission('team_engagement_manage') || hasPermission('manager') || hasPermission('settings')}
              />
            </LazyTab>
        )}

        {/* KASA */}
        {activeTab === "cash" && (hasPermission('cash_view') || hasPermission('cash_manage') || hasPermission('manager') || hasPermission('settings') || isManager) && (
          <LazyTab fallbackMessage="Wystąpił błąd w module Kasa">
              <CashTab
                location={location}
                canManage={isManager || hasPermission('cash_manage') || hasPermission('manager') || hasPermission('settings')}
              />
            </LazyTab>
        )}

        {/* PLIKI (Drive) — dostępne dla każdego zalogowanego */}
        {activeTab === "files" && (hasPermission('files') || hasPermission('manager') || hasPermission('settings') || isManager) && (
          <LazyTab fallbackMessage="Wystąpił błąd w module Pliki">
              <FilesTab location={location} user={user} />
            </LazyTab>
        )}

        {/* INWENTARYZACJA */}
        {activeTab === "inventory" && (hasPermission('inventory_view') || hasPermission('inventory_count') || hasPermission('inventory_manage') || hasPermission('manager') || hasPermission('settings') || isManager) && (
          <LazyTab fallbackMessage="Wystąpił błąd w module Inwentaryzacja">
              <InventoryTab
                location={location}
                canManage={isManager || hasPermission('inventory_manage') || hasPermission('manager') || hasPermission('settings')}
                currentUser={user?.username || ''}
              />
            </LazyTab>
        )}

        {/* ZAMKNIĘCIE MIESIĄCA */}
        {activeTab === "monthly_closing" && (hasPermission('monthly_closing_view') || hasPermission('monthly_closing_edit') || hasPermission('monthly_closing_finalize') || hasPermission('manager') || hasPermission('settings') || isManager) && (
          <LazyTab fallbackMessage="Wystąpił błąd w module Zamknięcie Miesiąca">
              <MonthlyClosingTab
                location={location}
                canEdit={isManager || hasPermission('monthly_closing_edit') || hasPermission('monthly_closing_finalize') || hasPermission('manager') || hasPermission('settings')}
                currentUser={user?.username || ''}
              />
            </LazyTab>
        )}

        {/* OPINIE RESTAURACJI */}
        {activeTab === "reviews" && (hasPermission('reviews_view') || hasPermission('reviews_edit') || hasPermission('manager') || hasPermission('settings') || isManager) && (
          <LazyTab fallbackMessage="Wystąpił błąd w module Opinie">
              <ReviewsTab
                location={location}
                canEdit={isManager || hasPermission('reviews_edit') || hasPermission('manager') || hasPermission('settings')}
              />
            </LazyTab>
        )}

        {/* REKRUTACJA INTERNETOWA (formularz online) */}
        {activeTab === "recruitment_apps" && (hasPermission('recruitment_apps_view') || hasPermission('manager') || hasPermission('settings') || isManager) && (
          <LazyTab fallbackMessage="Wystąpił błąd w module Rekrutacja internetowa">
              <RecruitmentApplicationsTab
                location={location}
                canEdit={isManager || hasPermission('recruitment_apps_edit') || hasPermission('manager') || hasPermission('settings')}
                currentUser={user?.username || ''}
              />
            </LazyTab>
        )}

        {/* ADD TIP */}
        {activeTab === "add" && hasPermission('tips') && (
          <LazyTab fallbackMessage="Wystąpił błąd w module Napiwki">
              <TipsTab 
                tipDate={tipDate}
                setTipDate={setTipDate}
                selectedWaiter={selectedWaiter}
                setSelectedWaiter={setSelectedWaiter}
                tipsAmount={tipsAmount}
                setTipsAmount={setTipsAmount}
                notes={notes}
                setNotes={setNotes}
                isDayClosed={isDayClosed}
                dayMeta={dayMeta}
                tipsWaiters={tipsWaiters}
                getCategoryDisplayName={getCategoryDisplayName}
                splitPreview={splitPreview}
                location={location}
                handleSaveEntry={handleSaveEntry}
                handleOpenDay={handleOpenDay}
                dayEntries={dayEntries}
                waiterById={waiterById}
              />
            </LazyTab>
        )}

        {/* SUMMARY */}
        {activeTab === "summary" && hasPermission('summary') && (
          <LazyTab tabKey="summary" fallbackMessage="Wystąpił błąd w module Podsumowanie">
                <SummaryTab 
                  tipDate={tipDate}
                  setTipDate={setTipDate}
                  dayTotals={dayTotals}
                  location={location}
                  isDayClosed={isDayClosed}
                  handleOpenDay={handleOpenDay}
                  handleCloseDay={handleCloseDay}
                  dayEntries={dayEntries}
                  waiterById={waiterById}
                  handleDeleteEntry={handleDeleteEntry}
                  handleRestoreEntry={handleRestoreEntry}
                  showDeletedEntries={showDeletedEntries}
                  setShowDeletedEntries={setShowDeletedEntries}
                />
              </LazyTab>
        )}

        {/* WAITERS */}
        {activeTab === "waiters" && hasPermission('employees') && (
          <LazyTab fallbackMessage="Wystąpił błąd w module Pracownicy">
              <EmployeesTab 
                firstName={firstName}
                setFirstName={setFirstName}
                lastName={lastName}
                setLastName={setLastName}
                branch={branch}
                setBranch={setBranch}
                birthDate={birthDate}
                setBirthDate={setBirthDate}
                workerCategory={workerCategory}
                setWorkerCategory={setWorkerCategory}
                jobPositions={jobPositions}
                handleAddWaiter={handleAddWaiter}
                displayWaiters={displayWaiters}
                showInactive={showInactive}
                setShowInactive={setShowInactive}
                expandedCategories={expandedCategories}
                setExpandedCategories={setExpandedCategories}
                getCategoryDisplayName={getCategoryDisplayName}
                handleSetWaiterStatus={handleSetWaiterStatus}
                handleRestoreWaiter={handleRestoreWaiter}
                showDeletedWaiters={showDeletedWaiters}
                setShowDeletedWaiters={setShowDeletedWaiters}
                location={location}
                API={API}
                refreshData={fetchDataLegacy}
              />
            </LazyTab>
        )}

        {/* REPORTS */}
        {activeTab === "reports" && hasPermission('reports') && (
          <LazyTab tabKey="reports" fallbackMessage="Wystąpił błąd w module Raporty">
                <ReportsTab 
                  location={location}
                  reportType={reportType}
                  setReportType={setReportType}
                  fromDate={fromDate}
                  setFromDate={setFromDate}
                  toDate={toDate}
                  setToDate={setToDate}
                  rangeLabel={rangeLabel}
                  rangeTotals={rangeTotals}
                  filteredEntries={filteredEntries}
                  waiterById={waiterById}
                  handleExportPDF={handleExportPDF}
                  handleExportCSV={handleExportCSV}
                  generateBillardPDF={generateBillardPDF}
                />
              </LazyTab>
        )}

        {/* BILLARD (only for Nowy Sącz) */}
        {activeTab === "billard" && location === "nowysacz" && hasPermission('billard') && (
          <LazyTab tabKey="billard" fallbackMessage="Wystąpił błąd w module Bilard">
                <BillardTab 
                  user={user}
                  location={location}
                  billardEntries={billardEntries}
                  fetchData={fetchDataLegacy}
                  handleRestoreBillard={handleRestoreBillard}
                  showDeletedBillard={showDeletedBillard}
                  setShowDeletedBillard={setShowDeletedBillard}
                />
              </LazyTab>
        )}

        {/* GLASS INVENTORY */}
        {activeTab === "glass" && hasPermission('glass') && (
          <LazyTab tabKey="glass" fallbackMessage="Wystąpił błąd w module Szkło">
                <GlassTab
                  user={user}
                  location={location}
                />
              </LazyTab>
        )}

        {/* KITCHEN LOSSES — STRATY (uprawnienia granularne) */}
        {/* Master `kitchen_losses` = pełen dostęp; albo dowolny z 3 sub-permów = wejście do menu */}
        {activeTab === "kitchen_losses" && (hasPermission('kitchen_losses') || hasPermission('straty_zgloszenia') || hasPermission('straty_raporty') || hasPermission('straty_protokoly')) && (() => {
          const subPerms = [hasPermission('straty_zgloszenia'), hasPermission('straty_raporty'), hasPermission('straty_protokoly')];
          const subCount = subPerms.filter(Boolean).length;
          const hasMaster = hasPermission('kitchen_losses');
          // Manager z masterem ALBO user/manager z jakimkolwiek sub-perm → pokaż menu kafelków
          // Tylko user bez sub-permów i bez mastera (NIE powinno się zdarzyć dzięki warunkowi powyżej) → fallback do formularza
          const showMenu = hasMaster || subCount > 0;
          return showMenu ? (
            <div style={{ padding: '16px' }}>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                {(hasPermission('kitchen_losses') || hasPermission('straty_zgloszenia')) && (
                  <button 
                    className="btn-primary"
                    onClick={() => setActiveTab('kitchen_losses_add')}
                    style={{ flex: '1 1 200px' }}
                  >
                    ➕ Dodaj Straty
                  </button>
                )}
                {(hasPermission('kitchen_losses') || hasPermission('straty_raporty')) && (
                  <button 
                    className="btn-primary"
                    onClick={() => setActiveTab('kitchen_losses_reports')}
                    style={{ flex: '1 1 200px' }}
                  >
                    📊 Raporty
                  </button>
                )}
                {(hasPermission('kitchen_losses') || hasPermission('straty_zgloszenia')) && (
                  <button 
                    className="btn-primary"
                    onClick={() => setActiveTab('kitchen_losses_products')}
                    style={{ flex: '1 1 200px' }}
                  >
                    📦 Baza produktów
                  </button>
                )}
                {(hasPermission('kitchen_losses') || hasPermission('straty_protokoly')) && (
                  <button 
                    className="btn-primary"
                    onClick={() => setActiveTab('warehouse_protocols')}
                    style={{ flex: '1 1 200px' }}
                  >
                    📋 Protokoły magazynowe
                  </button>
                )}
                {hasPermission('kitchen_losses') && (
                  <button 
                    onClick={() => setShowConversionModal(true)}
                    data-testid="kitchen-conversion-hub-btn"
                    style={{ 
                      flex: '1 1 200px',
                      padding: '14px',
                      background: 'linear-gradient(135deg, #3b82f6, #1e40af)',
                      color: '#fff', border: 'none', borderRadius: 10,
                      cursor: 'pointer', fontSize: 15, fontWeight: 700,
                    }}
                  >
                    🔄 Konwersja pliku
                  </button>
                )}
              </div>
              <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                Wybierz opcję powyżej
              </p>
            </div>
          ) : (
            <LazyTab tabKey="kitchen_losses" fallbackMessage="Wystąpił błąd w module Straty Kuchenne">
                  <KitchenLossesTab user={user} location={location} />
                </LazyTab>
          );
        })()}

        {activeTab === "kitchen_losses_add" && (hasPermission('kitchen_losses') || hasPermission('straty_zgloszenia')) && (
          <LazyTab tabKey="kitchen_losses_add" fallbackMessage="Wystąpił błąd w module Straty Kuchenne">
                <KitchenLossesTab user={user} location={location} />
              </LazyTab>
        )}

        {activeTab === "kitchen_losses_reports" && (hasPermission('kitchen_losses') || hasPermission('straty_raporty')) && (
          <LazyTab tabKey="kitchen_losses_reports" fallbackMessage="Wystąpił błąd w raporcie strat kuchennych">
                <KitchenLossesReportsTab user={user} location={location} />
              </LazyTab>
        )}

        {activeTab === "kitchen_losses_products" && (hasPermission('kitchen_losses') || hasPermission('straty_zgloszenia')) && (
          <LazyTab tabKey="kitchen_losses_products" fallbackMessage="Wystąpił błąd w bazie produktów">
                <KitchenLossesTab user={user} location={location} showProductsDatabase={true} />
              </LazyTab>
        )}

        {activeTab === "warehouse_protocols" && (hasPermission('kitchen_losses') || hasPermission('straty_protokoly')) && (
          <LazyTab tabKey="warehouse_protocols" fallbackMessage="Wystąpił błąd w protokołach magazynowych">
                <WarehouseProtocolsTab user={user} location={location} />
              </LazyTab>
        )}

        {activeTab === "jerry_drive" && hasPermission('jerry_drive') && (
          <LazyTab tabKey="jerry_drive" fallbackMessage="Wystąpił błąd w Jerry Drive">
                <JerryDriveTab user={user} location={location} />
              </LazyTab>
        )}

        {activeTab === "warehouse" && location === 'grodek' && (hasPermission('warehouse_view') || hasPermission('warehouse_manage') || hasPermission('manager') || hasPermission('settings') || isManager) && (
          <LazyTab tabKey="warehouse" fallbackMessage="Wystąpił błąd w module Magazyn">
                <WarehouseTab location={location} currentUser={user} />
              </LazyTab>
        )}

        {activeTab === "restaurant_matters" && (hasPermission('restaurant_matters') || user?.role === 'admin') && (
          <LazyTab tabKey="restaurant_matters" fallbackMessage="Wystąpił błąd w module Sprawy restauracji">
                <RestaurantMattersTab location={location} currentUser={user} />
              </LazyTab>
        )}

        {activeTab === "events_catering" && (hasPermission('events_catering') || hasPermission('manager') || hasPermission('settings') || user?.role === 'admin') && (
          <LazyTab tabKey="events_catering" fallbackMessage="Wystąpił błąd w module Konfigurator przyjęć">
                <EventsCateringTab location={location} user={user} />
              </LazyTab>
        )}

        {activeTab === "delivery_survey" && location === 'nowysacz' && (hasPermission('delivery_survey') || hasPermission('delivery_complaints_only') || hasPermission('manager') || hasPermission('settings') || user?.role === 'admin') && (
          <LazyTab tabKey="delivery_survey" fallbackMessage="Wystąpił błąd w module Ankiety zadowolenia">
                <DeliverySurveyTab user={user} hasPermission={hasPermission} />
              </LazyTab>
        )}

        {activeTab === "club_admin" && (hasPermission('club_admin') || hasPermission('manager') || hasPermission('settings') || user?.role === 'admin') && (
          <LazyTab tabKey="club_admin" fallbackMessage="Wystąpił błąd w module MR JERRY CLUB">
                <ClubAdminTab />
              </LazyTab>
        )}

        {activeTab === "invoices" && (hasPermission('invoices_view') || hasPermission('invoices_manage') || hasPermission('manager') || hasPermission('settings') || user?.role === 'admin') && (
          <LazyTab tabKey="invoices" fallbackMessage="Wystąpił błąd w module Faktury i płatności">
                <InvoicesTab location={location} />
              </LazyTab>
        )}

        {/* Beer Cleaning Tab - Available for both locations */}
        {activeTab === "beer_cleaning" && (
          <LazyTab tabKey="beer_cleaning" fallbackMessage="Wystąpił błąd w module Czyszczenie Piwa">
                <BeerCleaningTab user={user} location={location} />
              </LazyTab>
        )}

        {/* TRAINING AI */}
        {activeTab === "training" && hasPermission('training') && (
          <LazyTab tabKey="training" fallbackMessage="Wystąpił błąd w module Szkolenia">
                <TrainingTab user={user} location={location} />
              </LazyTab>
        )}

        {/* MATERIALS (LEARNING RESOURCES) */}
        {activeTab === "materials" && hasPermission('materials_view') && (
          <LazyTab tabKey="materials" fallbackMessage="Wystąpił błąd w module Materiały">
                <MaterialsTab user={user} location={location} />
              </LazyTab>
        )}

        {/* DAILY BRIEFING (KARTA ODPRAWY) */}
        {activeTab === "briefing" && hasPermission('briefing_view') && (
          <LazyTab tabKey="briefing" fallbackMessage="Wystąpił błąd w module Informacje Dnia">
                <DailyBriefingTab user={user} location={location} />
              </LazyTab>
        )}

        {/* SALES MODULE */}
        {activeTab === "sales" && (
          <LazyTab tabKey="sales" fallbackMessage="Wystąpił błąd w module Sprzedaż">
                <SalesTab user={user} location={location} />
              </LazyTab>
        )}

        {/* PROMOTIONS */}
        {activeTab === "promotions" && (
          <LazyTab tabKey="promotions" fallbackMessage="Wystąpił błąd w module Promocje">
                <PromotionsTab user={user} location={location} />
              </LazyTab>
        )}

        {/* MARKETING */}
        {activeTab === "marketing" && (
          <LazyTab tabKey="marketing" fallbackMessage="Wystąpił błąd w module Marketing">
                <MarketingTab user={user} location={location} />
              </LazyTab>
        )}

        {/* KONTROLA ROZLICZEŃ DZIENNYCH */}
        {activeTab === "daily_settlement" && (
          <LazyTab tabKey="daily_settlement" fallbackMessage="Wystąpił błąd w module Rozliczenia Dzienne">
            <DailySettlementTab user={user} location={location} />
          </LazyTab>
        )}

        {/* DELIVERIES (ZAKUPY) - Kraków only */}
        {activeTab === "deliveries" && location === 'krakow' && hasPermission('deliveries_view') && (
          <LazyTab fallbackMessage="Wystąpił błąd w module Dostawy">
              <DeliveriesTab user={user} location={location} />
            </LazyTab>
        )}

        {/* GRAPHIC PROJECTS - Nowy Sącz only */}
        {activeTab === "graphic_projects" && location === 'nowysacz' && hasPermission('graphic_projects') && (
          <LazyTab tabKey="graphic_projects" fallbackMessage="Wystąpił błąd w module Grafika">
                <GraphicProjectsTab user={user} location={location} />
              </LazyTab>
        )}

        {/* CLOTHING */}
        {activeTab === "clothing" && (hasPermission('clothing') || hasPermission('clothing_add')) && (
          <LazyTab tabKey="clothing" fallbackMessage="Wystąpił błąd w module Odzież">
                <ClothingTab 
                  user={user}
                  location={location}
                  clothingItems={clothingItems}
                  workers={waiters}
                  clothingTypes={clothingTypes}
                  fetchData={fetchDataLegacy}
                  handleRestoreClothing={handleRestoreClothing}
                  showDeletedClothing={showDeletedClothing}
                  setShowDeletedClothing={setShowDeletedClothing}
                  canFullAccess={hasPermission('clothing') || isManager || hasPermission('settings')}
                />
              </LazyTab>
        )}

        {/* EXPORT */}
        {activeTab === "export" && hasPermission('export') && (
          <LazyTab tabKey="export" fallbackMessage="Wystąpił błąd w module Eksport">
                <ExportTab 
                  exportFilter={exportFilter}
                  setExportFilter={setExportFilter}
                  closedDays={closedDays}
                  handleClearOldExports={handleClearOldExports}
                  entries={entries}
                  generateDayPDF={generateDayPDF}
                  generateBillardPDF={generateBillardPDF}
                  location={location}
                />
              </LazyTab>
        )}


        {/* CHANGELOG */}
        {activeTab === "changelog" && (
          <LazyTab tabKey="changelog" fallbackMessage="Wystąpił błąd w module Changelog">
                <ChangelogTab />
              </LazyTab>
        )}

        {/* TUTORIAL */}
        {activeTab === "tutorial" && (
          <LazyTab fallbackMessage="Wystąpił błąd w Samouczku">
              <TutorialTab />
            </LazyTab>
        )}

        {/* SETTINGS */}
        {activeTab === "settings" && hasPermission('settings') && (
          <LazyTab fallbackMessage="Wystąpił błąd w module Ustawienia">
              <SettingsTab 
                user={user}
                location={location}
                isManager={isManager}
                newJobPosition={newJobPosition}
                setNewJobPosition={setNewJobPosition}
                handleAddJobPosition={handleAddJobPosition}
                jobPositions={jobPositions}
                handleDeleteJobPosition={handleDeleteJobPosition}
                editingJobPositionId={editingJobPositionId}
                setEditingJobPositionId={setEditingJobPositionId}
                editJobPositionName={editJobPositionName}
                setEditJobPositionName={setEditJobPositionName}
                handleUpdateJobPosition={handleUpdateJobPosition}
                newClothingType={newClothingType}
                setNewClothingType={setNewClothingType}
                handleAddClothingType={handleAddClothingType}
                clothingTypes={clothingTypes}
                handleDeleteClothingType={handleDeleteClothingType}
                editingClothingTypeId={editingClothingTypeId}
                setEditingClothingTypeId={setEditingClothingTypeId}
                editClothingTypeName={editClothingTypeName}
                setEditClothingTypeName={setEditClothingTypeName}
                handleUpdateClothingType={handleUpdateClothingType}
                daysToKeep={daysToKeep}
                setDaysToKeep={setDaysToKeep}
                handleCleanupData={handleCleanupData}
                lastBackupDate={lastBackupDate}
                showBackupReminder={showBackupReminder}
                handleBackupDownload={handleBackupDownload}
                restoreFileRef={restoreFileRef}
                handleRestoreFile={handleRestoreFile}
                handleConfirmBackup={handleConfirmBackup}
                handleResetAll={handleResetAll}
                archivedBackups={archivedBackups}
                handleRestoreFromArchive={handleRestoreFromArchive}
                handleDeleteArchivedBackup={handleDeleteArchivedBackup}
                auditLogs={auditLogs}
                expandSystemLogs={expandSystemLogs}
                setExpandSystemLogs={setExpandSystemLogs}
                expandJobPositions={expandJobPositions}
                setExpandJobPositions={setExpandJobPositions}
                expandClothingTypes={expandClothingTypes}
                setExpandClothingTypes={setExpandClothingTypes}
                expandGlassTypes={expandGlassTypes}
                setExpandGlassTypes={setExpandGlassTypes}
                newGlassType={newGlassType}
                setNewGlassType={setNewGlassType}
                handleAddGlassType={handleAddGlassType}
                glassTypes={glassTypes}
                handleDeleteGlassType={handleDeleteGlassType}
                editingGlassTypeId={editingGlassTypeId}
                setEditingGlassTypeId={setEditingGlassTypeId}
                editGlassTypeName={editGlassTypeName}
                setEditGlassTypeName={setEditGlassTypeName}
                handleUpdateGlassType={handleUpdateGlassType}
              />
            </LazyTab>
        )}

        {/* MANAGER - Employee Management */}
        {activeTab === "manager" && (user?.role === "admin" || user?.role === "manager" || hasPermission('manager') || hasPermission('settings') || isManager) && (
          <LazyTab fallbackMessage="Wystąpił błąd w module Manager">
              <ManagerTab
                user={user}
                location={location}
                refreshEmployeesData={fetchDataLegacy}
                waiters={waiters}
                displayWaiters={displayWaiters}
                waiterFullName={waiterFullName}
                showInactive={showInactive}
                setShowInactive={setShowInactive}
                onRefreshReady={(refreshFn) => { managerRefreshRef.current = refreshFn; }}
              />
            </LazyTab>
        )}

        {/* DEVELOPMENT TEAM - Ideas & Problems */}
        {activeTab === "development" && (location === "nowysacz" || user?.username === 'Developer') && hasPermission('settings') && (
          <LazyTab tabKey="development" fallbackMessage="Wystąpił błąd w module Zespół rozwoju">
                <DevelopmentTab location={location} user={user} />
              </LazyTab>
        )}

        {/* RECRUITMENT */}
        {activeTab === "recruitment" && (hasPermission('recruitment') || hasPermission('recruitment_view')) && (
          <LazyTab tabKey="recruitment" fallbackMessage="Wystąpił błąd w module Rekrutacja">
                <RecruitmentTab
                  location={location}
                  jobPositions={jobPositions}
                  readOnly={!hasPermission('recruitment') && hasPermission('recruitment_view')}
                />
              </LazyTab>
        )}
        </div>
      </main>

      {/* Globalny widget Komunikatora (FAB w prawym dolnym rogu) */}
      <CommunicatorWidget
        user={user}
        location={location}
        unreadCount={communicatorUnread}
      />

      {/* Globalny FAB „Zgłoś sprawę do managera" */}
      <MattersFab user={user} location={location} />
      <SalesRankingPopup user={user} location={location} />

      {/* Backup Reminder Modal */}
      {showBackupReminder && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'var(--bg-card)',
            padding: '30px',
            borderRadius: '16px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
            border: '3px solid var(--gold)',
            animation: 'pulse 2s infinite',
            position: 'relative'
          }}>
            {/* Close button */}
            <button 
              onClick={() => setShowBackupReminder(false)}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: 'var(--text-muted)',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px'
              }}
              title="Zamknij (pojawi się ponownie za minutę)"
            >
              ✕
            </button>

            <div style={{textAlign: 'center'}}>
              <div style={{fontSize: '60px', marginBottom: '15px'}}>⚠️</div>
              <h2 style={{color: 'var(--gold)', marginBottom: '15px', fontSize: '24px'}}>
                🗄️ WAŻNE: KOPIA ZAPASOWA BAZY!
              </h2>
              <p style={{fontSize: '16px', marginBottom: '10px', lineHeight: '1.6'}}>
                {lastBackupDate 
                  ? `Ostatnia kopia zapasowa była ${Math.floor((new Date() - new Date(lastBackupDate)) / (1000 * 60 * 60 * 24))} dni temu.`
                  : 'Nie wykonano jeszcze żadnej kopii zapasowej bazy danych.'
                }
              </p>
              <p style={{fontSize: '16px', marginBottom: '20px', lineHeight: '1.6', color: '#ff6b6b', fontWeight: '600'}}>
                ⚠️ Bez kopii zapasowej ryzykujesz utratę wszystkich danych restauracji!
              </p>
              
              <div style={{
                background: 'rgba(212, 175, 55, 0.1)',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '1px solid rgba(212, 175, 55, 0.3)'
              }}>
                <p style={{fontSize: '14px', margin: 0, color: 'var(--text-muted)'}}>
                  💡 <strong>Przejdź do Ustawień</strong> aby wykonać kopię zapasową.<br/>
                  Po wykonaniu backupu potwierdź to w sekcji "Backup bazy danych".
                </p>
              </div>

              <button 
                className="btn-primary btn-xlarge"
                onClick={() => {
                  setActiveTab('settings');
                  setShowBackupReminder(false); // Hide modal when going to settings
                }}
                style={{marginBottom: '10px', width: '100%'}}
              >
                ⚙️ Przejdź do Ustawień
              </button>
              
              <p style={{fontSize: '12px', color: 'var(--text-muted)', marginTop: '15px'}}>
                ⏰ Przypomnienie pojawi się ponownie za minutę (dopóki nie wykonasz backupu)
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Changelog Modal */}
      <ChangelogModal 
        isOpen={isChangelogOpen} 
        onClose={() => setIsChangelogOpen(false)} 
      />

      {/* Push Notifications Prompt (after login, non-blocking) */}
      <PushNotificationPrompt user={user} location={location} />
      <ForceReacceptanceModal user={user} />

      {/* Suggestion Modal */}
      {showSuggestionModal && (
        <SuggestionModal
          user={user}
          location={location}
          onClose={() => setShowSuggestionModal(false)}
        />
      )}

      {/* Inventory Conversion Modal (globalny — dostępny z hubu Strat) */}
      {showConversionModal && (
        <Suspense fallback={null}>
          <InventoryConversionModal onClose={() => setShowConversionModal(false)} />
        </Suspense>
      )}

      {/* Mobile Navigation - Only on mobile */}
      {isMobile && (
        <HamburgerMenu 
            menuItems={(() => {
              const items = [];
              
              // Generate all menu items based on permissions
              // 'dashboard' (Start) — tymczasowo wyłączone w 2.2.8
              items.push({ id: 'communicator', icon: '💬', label: 'Komunikator', active: activeTab === 'communicator', badge: communicatorUnread > 0 && activeTab !== 'communicator' ? communicatorUnread : 0 });
              items.push({ id: 'reminders', icon: '🔔', label: 'Przypomnienia', active: activeTab === 'reminders', badge: remindersCount > 0 && activeTab !== 'reminders' ? remindersCount : 0 });
              if (hasPermission('tips')) items.push({ id: 'add', icon: '💰', label: 'Dodaj napiwek', active: activeTab === 'add' });
              if (hasPermission('summary')) items.push({ id: 'summary', icon: '📊', label: 'Podsumowanie', active: activeTab === 'summary' });
              if (hasPermission('reports')) items.push({ id: 'reports', icon: '📈', label: 'Raporty', active: activeTab === 'reports' });
              if (hasPermission('employees')) items.push({ id: 'waiters', icon: '👥', label: 'Pracownicy', active: activeTab === 'waiters' });
              if (location === 'nowysacz' && hasPermission('billard')) items.push({ id: 'billard', icon: '🎱', label: 'Billard', active: activeTab === 'billard' });
              if (location === 'nowysacz' && hasPermission('graphic_projects')) items.push({ id: 'graphic_projects', icon: '🎨', label: 'Grafika', active: activeTab === 'graphic_projects' });
              if (hasPermission('glass')) items.push({ id: 'glass', icon: '🍷', label: 'Szkła', active: activeTab === 'glass' });
              if (hasPermission('kitchen_losses') || hasPermission('straty_zgloszenia') || hasPermission('straty_raporty') || hasPermission('straty_protokoly')) items.push({ id: 'kitchen_losses', icon: '🔪', label: 'Straty', active: activeTab === 'kitchen_losses' });
              if (hasPermission('jerry_drive') && location === 'nowysacz') items.push({ id: 'jerry_drive', icon: '🚗', label: 'Jerry Drive', active: activeTab === 'jerry_drive' });
              if (location === 'grodek' && (hasPermission('warehouse_view') || hasPermission('warehouse_manage') || hasPermission('manager') || hasPermission('settings'))) items.push({ id: 'warehouse', icon: '📦', label: 'Magazyn', active: activeTab === 'warehouse' });
              if (hasPermission('beer_cleaning')) items.push({ id: 'beer_cleaning', icon: '🍺', label: 'Czyszczenie piwa', active: activeTab === 'beer_cleaning' });
              if (hasPermission('training')) items.push({ id: 'training', icon: '📚', label: 'Szkolenie', active: activeTab === 'training' });
              if (hasPermission('materials_view')) items.push({ id: 'materials', icon: '📖', label: 'Materiały', active: activeTab === 'materials' });
              if (hasPermission('briefing_view')) items.push({ id: 'briefing', icon: '📋', label: 'Informacje Dnia', active: activeTab === 'briefing' });
              if (hasPermission('sales')) items.push({ id: 'sales', icon: '💰', label: 'Sprzedaż', active: activeTab === 'sales' });
              if (hasPermission('promotions')) items.push({ id: 'promotions', icon: '🎁', label: 'Promocje', active: activeTab === 'promotions' });
              if (hasPermission('marketing')) {
                items.push({ id: 'marketing', icon: '📣', label: 'Marketing', active: activeTab === 'marketing' });
              }
              if (location === 'krakow' && hasPermission('deliveries_view')) items.push({ id: 'deliveries', icon: '📦', label: 'Zakupy', active: activeTab === 'deliveries' });
              if (hasPermission('clothing') || hasPermission('clothing_add')) items.push({ id: 'clothing', icon: '👕', label: 'Odzież', active: activeTab === 'clothing' });
              if (hasPermission('forms')) items.push({ id: 'forms', icon: '📋', label: 'Formularze', active: activeTab === 'forms' });
              if (hasPermission('export')) items.push({ id: 'export', icon: '💾', label: 'Eksport', active: activeTab === 'export' });
              // Samouczek — wyłączony tymczasowo
              // items.push({ id: 'tutorial', icon: '📚', label: 'Samouczek', active: activeTab === 'tutorial' });
              if (hasPermission('manager')) items.push({ id: 'manager', icon: '👔', label: 'Manager', active: activeTab === 'manager' });
              if (hasPermission('manager_analytics') || hasPermission('manager_analytics_view') || hasPermission('manager_analytics_edit') || hasPermission('manager') || hasPermission('settings')) items.push({ id: 'manager_analytics', icon: '📈', label: 'Analiza managerska', active: activeTab === 'manager_analytics' });
              if (hasPermission('manager_performance_view') || hasPermission('manager_performance_edit') || hasPermission('manager_performance_owner') || hasPermission('manager') || hasPermission('settings')) {
                items.push({ id: 'manager_performance', icon: '🎯', label: 'Panel kierownika restauracji', active: activeTab === 'manager_performance' });
              }
              if (location === 'nowysacz' && (hasPermission('delikatesy') || hasPermission('delikatesy_view') || hasPermission('delikatesy_edit') || hasPermission('manager') || hasPermission('settings'))) {
                items.push({ id: 'delikatesy', icon: '🥖', label: 'Delikatesy', active: activeTab === 'delikatesy' });
              }
              if (hasPermission('team_engagement') || hasPermission('team_engagement_manage') || hasPermission('manager') || hasPermission('settings')) {
                items.push({ id: 'team_engagement', icon: '🤝', label: 'Zaangażowanie zespołu', active: activeTab === 'team_engagement' });
              }
              if (hasPermission('cash_view') || hasPermission('cash_manage') || hasPermission('manager') || hasPermission('settings')) {
                items.push({ id: 'cash', icon: '💰', label: 'Kasa', active: activeTab === 'cash' });
              }
              if (hasPermission('inventory_view') || hasPermission('inventory_count') || hasPermission('inventory_manage') || hasPermission('manager') || hasPermission('settings')) {
                items.push({ id: 'inventory', icon: '📦', label: 'Inwentaryzacja', active: activeTab === 'inventory' });
              }
              if (hasPermission('monthly_closing_view') || hasPermission('monthly_closing_edit') || hasPermission('monthly_closing_finalize') || hasPermission('manager') || hasPermission('settings')) {
                items.push({ id: 'monthly_closing', icon: '🗓️', label: 'Zamknięcie miesiąca', active: activeTab === 'monthly_closing' });
              }
              if (hasPermission('reviews_view') || hasPermission('reviews_edit') || hasPermission('manager') || hasPermission('settings')) {
                items.push({ id: 'reviews', icon: '⭐', label: 'Opinie', active: activeTab === 'reviews' });
              }
              if (hasPermission('recruitment') || hasPermission('recruitment_view')) {
                items.push({ id: 'recruitment', icon: '📅', label: 'Dni próbne', active: activeTab === 'recruitment' });
              }
              if (hasPermission('recruitment_apps_view') || hasPermission('manager') || hasPermission('settings')) {
                items.push({ id: 'recruitment_apps', icon: '🧑‍💼', label: 'Rekrutacja internetowa', active: activeTab === 'recruitment_apps' });
              }
              if (hasPermission('restaurant_matters') || user?.role === 'admin') {
                items.push({ id: 'restaurant_matters', icon: '📋', label: 'Sprawy restauracji', active: activeTab === 'restaurant_matters' });
              }
              if (hasPermission('invoices_view') || hasPermission('invoices_manage') || hasPermission('manager') || hasPermission('settings') || user?.role === 'admin') {
                items.push({ id: 'invoices', icon: '💰', label: 'Faktury i płatności', active: activeTab === 'invoices' });
              }
              if (hasPermission('events_catering') || hasPermission('manager') || hasPermission('settings') || user?.role === 'admin') {
                items.push({ id: 'events_catering', icon: '🎉', label: 'Konfigurator przyjęć', active: activeTab === 'events_catering' });
              }
              if (location === 'nowysacz' && (hasPermission('delivery_survey') || hasPermission('manager') || hasPermission('settings') || user?.role === 'admin')) {
                items.push({ id: 'delivery_survey', icon: '🎯', label: 'Ankiety zadowolenia', active: activeTab === 'delivery_survey' });
              }
              if (hasPermission('manager') || hasPermission('settings') || user?.role === 'admin') {
                items.push({ id: 'club_admin', icon: '🥃', label: 'MR JERRY CLUB', active: activeTab === 'club_admin' });
              }
              // Development Tab - tylko dla Developera lub Nowy Sącz z uprawnieniami
              if ((user?.username === 'Developer' || user?.role === 'developer') || (location === 'nowysacz' && hasPermission('settings'))) {
                items.push({ id: 'development', icon: '👥', label: 'Zespół rozwoju', active: activeTab === 'development' });
              }
              if (hasPermission('settings')) items.push({ id: 'settings', icon: '⚙️', label: 'Ustawienia', active: activeTab === 'settings' });
              
              return items;
            })()}
            onMenuItemClick={(item) => {
              setActiveTab(item.id);
            }}
            currentLocation={location}
            isOpen={isHamburgerOpen}
            onToggle={setIsHamburgerOpen}
            hideButton={true}
          />
      )}
    </div>
  );
}

export default MainApp;
